import {
	FIXEDCACHEKEYS,
	handleApiError,
	HTTPSTATUSCODE,
	type ILoginError,
	type LoginSchemaType,
	type OTPTYPE,
} from 'BreetHelpers';
import { useLoginSuccess } from 'BreetHooks';
import { setCredentials, useAppDispatch, useLoginMutation } from 'BreetRedux';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import { useModalApi } from './useModalApi';

export const useLoginApi = () => {
	const navigate = useNavigate();

	const [login, { isLoading }] = useLoginMutation({ fixedCacheKey: FIXEDCACHEKEYS.LOGIN });

	const dispatch = useAppDispatch();

	const { closeModalDirect, openModalDirect } = useModalApi();

	const { onLoginSuccess } = useLoginSuccess();

	const handleLoginResponse = useCallback(
		(payload: LoginSchemaType & { businessId?: string }, statusCode?: number, otpType?: OTPTYPE) => {
			const is2faRequired = statusCode === HTTPSTATUSCODE.TWOFA;
			const isNewDevice = statusCode === HTTPSTATUSCODE.NEWDEVICE;

			if (is2faRequired || isNewDevice) {
				dispatch(
					setCredentials({
						email: payload.email,
						password: payload.password,
						businessId: payload.businessId,
						otpType,
					})
				);
				navigate(otpType !== 'authenticator' && isNewDevice ? '/new-device' : '/2fa')?.catch(console.error);
				return;
			}

			onLoginSuccess();
		},
		[dispatch, navigate, onLoginSuccess]
	);

	const loginUser = useCallback(
		(payload: LoginSchemaType & { businessId?: string; onFinalCallback?: () => void }) => {
			const { onFinalCallback, ...rest } = payload;

			openModalDirect({ id: 'loadingDialog', dialogMessage: 'Authenticating User' });

			login(rest)
				.unwrap()
				.then((data) => {
					handleLoginResponse(rest, data.statusCode, data.data?.otpCodeType);
				})
				.catch((e: unknown) => {
					const errResponse = e as ILoginError;
					const isMultipleTeams = errResponse.status === HTTPSTATUSCODE.MULTIPLETEAMS;

					if (isMultipleTeams) {
						dispatch(
							setCredentials({
								email: payload.email,
								password: payload.password,
								businessId: payload.businessId,
							})
						);

						openModalDirect({ id: 'switchUserAccount' });
						return;
					}

					handleApiError(e);
				})
				.finally(() => {
					closeModalDirect('loadingDialog');
					onFinalCallback?.();
				});
		},
		[closeModalDirect, dispatch, handleLoginResponse, login, openModalDirect]
	);

	return {
		loginUser,
		isLoading,
	};
};
